import { useEffect, useMemo, useRef } from 'react'
import useWebSocketImport from 'react-use-websocket'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { asyncJobQueries } from 'src/services/async-jobs/queries'
import { asyncJobApi } from 'src/services/async-jobs/api'
import { useAsyncJobStore } from 'src/stores/async-jobs'
import { useJobToast } from 'src/components/toast-helpers'
import { getInvalidateKeys } from 'src/services/async-jobs/task-type-registry'
import {
  createJobEventProcessor,
  createJobsWsConsumer,
  type JobEventProcessor,
  type JobEventProcessorDeps,
} from 'src/services/async-jobs/job-events'
import { useAuth } from 'src/stores/auth'

// react-use-websocket is CJS-only. Vite 8/Rolldown's dep optimizer resolves its
// `export default require_dist()` to the module namespace object rather than
// `exports.default`, so the default import can arrive wrapped. Unwrap defensively —
// works whether or not the interop is fixed upstream.
const useWebSocket =
  (useWebSocketImport as { default?: typeof useWebSocketImport }).default ?? useWebSocketImport

const MAX_RECONNECT_DELAY_MS = 30000
// Coarse safety-net poll that runs *alongside* the websocket, not only when it's
// down. The socket is the fast path; this backstop catches any terminal event
// the socket dropped (no redelivery / no dead-letter queue). Intentionally slow
// — ~20x lighter than the old 3s stream — since push handles the common case.
const SAFETY_POLL_INTERVAL_MS = 30000

export const AsyncJobTracker = () => {
  const token = useAuth.use.token()
  const { replaceActive, pushRecent, removeFromActive } = useAsyncJobStore((s) => s.actions)
  const { notifyJobTerminal } = useJobToast()
  const queryClient = useQueryClient()

  // Keep the latest volatile deps in a ref so the processor can be built once
  // and still call current handlers without stale closures.
  const depsRef = useRef<JobEventProcessorDeps>({} as JobEventProcessorDeps)
  depsRef.current = {
    pushRecent,
    notifyJobTerminal,
    invalidateForJob: (job) => {
      for (const key of getInvalidateKeys(job)) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
    removeFromActive,
    replaceActive,
    retrieve: (id) => asyncJobApi.retrieve(id),
    listActive: async () => {
      const res = await asyncJobApi.list({ filters: { status: 'active' } })
      return res.results
    },
    getKnownActiveIds: () => useAsyncJobStore.getState().active.map((j) => j.id),
  }

  const processorRef = useRef<JobEventProcessor | null>(null)
  if (!processorRef.current) {
    processorRef.current = createJobEventProcessor(() => depsRef.current)
  }
  const processor = processorRef.current

  // tn-models ws consumer: transforms (camelCase + validate) each raw `ws/jobs/`
  // payload, then hands the terminal-job orchestration to the processor.
  const wsConsumerRef = useRef<ReturnType<typeof createJobsWsConsumer> | null>(null)
  if (!wsConsumerRef.current) {
    wsConsumerRef.current = createJobsWsConsumer((job) => processor.processTerminal(job))
  }
  const wsConsumer = wsConsumerRef.current

  const webSocketUrl = useMemo(() => {
    if (!token) return null
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    return `${protocol}//${host}/ws/jobs/?token=${token}`
  }, [token])

  const { lastJsonMessage } = useWebSocket(webSocketUrl, {
    // On every (re)connect, one reconcile fetch catches jobs that finished
    // while the socket was down.
    onOpen: () => {
      processor.reconcile()
    },
    // Reconnect with exponential backoff after an unexpected drop.
    shouldReconnect: () => true,
    reconnectAttempts: Number.MAX_SAFE_INTEGER,
    reconnectInterval: (attempt) => Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS),
  })

  // ws terminal-event path: feed the raw payload through the tn-models consumer,
  // which transforms it and routes terminal jobs to the processor. No per-job retrieve.
  useEffect(() => {
    if (lastJsonMessage) {
      wsConsumer.feed(lastJsonMessage)
    }
  }, [lastJsonMessage, wsConsumer])

  // Safety poll: runs alongside the socket (not only when it's down) so a
  // terminal event the socket dropped is still reconciled within one interval.
  // Both paths funnel through the same processor's `processed` set, so nothing
  // double-toasts. Background tabs are excluded to keep it cheap.
  const { data: activeJobs } = useQuery({
    ...asyncJobQueries.listActive(),
    refetchInterval: SAFETY_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    enabled: Boolean(token),
  })

  useEffect(() => {
    if (activeJobs) {
      processor.ingestActive(activeJobs)
    }
  }, [activeJobs, processor])

  return null
}
